import { useEffect, useState } from 'react';
import { getLastProjectId, useProjectIdFromPath } from '../../lib/project-path';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import RichTextField from './RichTextField';
import { isRichTextEmpty } from '../../lib/rich-text';
import { uploadPendingAttachment } from '../../lib/attachments';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { apiFetch, v1Path } from '../../lib/api';
import { isWorkspaceProject } from '../../lib/workspace-projects';
import {
  DEFAULT_WORK_ITEM_SUBTYPES,
  WORK_ITEM_TYPES,
  workItemTypeLabel,
  type MemberDto,
  type ProjectDto,
  type StateDto,
  type WorkItemFull,
  type WorkItemSubtype,
  type WorkItemSubtypeDefinition,
} from '../../lib/work-types';
import { useOverlays } from './overlay-context';
import { useSession } from '../session-context';
import InitiativePicker from './InitiativePicker';
import { OwnerPicker } from './InitiativeOwners';
import { createLaunchStep, fetchInitiativeStreams, type InitiativeStream } from '../../lib/initiative-streams';

export default function CreateWorkItemModal() {
  const pathProjectId = useProjectIdFromPath();
  const { userId } = useSession();
  const {
    createWorkItem,
    setCreateWorkItemOpen,
    openCreateWorkItem,
    confirm,
    toast,
  } = useOverlays();
  const launchStep = createWorkItem.launchStep ?? null;
  const lockedType = createWorkItem.type ?? null;
  // A launch plan lives on the Initiatives workspace, so its path is never the step's stream.
  const contextProjectId = launchStep ? undefined : pathProjectId;
  const [chosenProjectId, setChosenProjectId] = useState(
    createWorkItem.projectId ?? contextProjectId ?? '',
  );
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [stateId, setStateId] = useState(createWorkItem.stateId ?? '');
  const [states, setStates] = useState<StateDto[]>([]);
  const [subtypes, setSubtypes] = useState<WorkItemSubtypeDefinition[]>(DEFAULT_WORK_ITEM_SUBTYPES);
  const [itemType, setItemType] = useState<(typeof WORK_ITEM_TYPES)[number]>(
    createWorkItem.type ?? 'task',
  );
  const [subtype, setSubtype] = useState<WorkItemSubtype | ''>('');
  const [initiativeId, setInitiativeId] = useState<string | null>(null);
  const [goLiveDate, setGoLiveDate] = useState('');
  const [ownerIds, setOwnerIds] = useState<string[]>([]);
  const [streams, setStreams] = useState<InitiativeStream[]>([]);
  const [teamMembers, setTeamMembers] = useState<MemberDto[]>([]);
  const ownerOptions = launchStep
    ? (streams.find((stream) => stream.id === chosenProjectId)?.members ?? [])
    : teamMembers;
  const canLinkInitiative = itemType === 'task' && !launchStep;
  const [createMore, setCreateMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const dirty = title.trim() !== '' || !isRichTextEmpty(description);

  useEffect(() => {
    if (!createWorkItem.open) return;
    const nextType = createWorkItem.type ?? 'task';
    setTitle('');
    setDescription('');
    setStateId(createWorkItem.stateId ?? '');
    setItemType(nextType);
    setSubtype('');
    setInitiativeId(createWorkItem.launchStep?.initiativeId ?? null);
    setGoLiveDate('');
    setOwnerIds([]);
    const initialProject = createWorkItem.projectId ?? contextProjectId ?? '';
    setChosenProjectId(initialProject);
    let cancelled = false;
    if (createWorkItem.launchStep) {
      // Anyone can put a launch step in any live stream, including streams they are not in.
      // Its stream is a deliberate choice, so it starts empty.
      void fetchInitiativeStreams()
        .then((items) => {
          if (cancelled) return;
          setStreams(items);
          setProjects(items);
        })
        .catch(() => {
          if (!cancelled) setProjects([]);
        });
      return () => {
        cancelled = true;
      };
    }
    void apiFetch<{ items: ProjectDto[] }>(v1Path('/projects?limit=100&offset=0'))
      .then((res) => {
        if (cancelled) return;
        // A department step can go to any live stream, so it is not limited to the ones you are in.
        const members = res.items.filter((project) => (
          createWorkItem.capture
            ? !project.archived_at && project.identifier !== 'SR'
            : !isWorkspaceProject(project) && project.is_member !== false
        ));
        setProjects(members);
        const last = getLastProjectId();
        const preferred = members.find((project) => project.id === last) ?? members[0];
        if (preferred) setChosenProjectId(preferred.id);
      })
      .catch(() => {
        if (!cancelled) setProjects([]);
      });
    return () => {
      cancelled = true;
    };
  }, [
    createWorkItem.open,
    createWorkItem.projectId,
    createWorkItem.stateId,
    createWorkItem.type,
    createWorkItem.launchStep,
    createWorkItem.capture,
    contextProjectId,
  ]);

  useEffect(() => {
    if (!createWorkItem.open || !chosenProjectId) {
      setTeamMembers([]);
      setStates([]);
      setSubtypes(DEFAULT_WORK_ITEM_SUBTYPES);
      return;
    }
    if (launchStep) {
      const stream = streams.find((row) => row.id === chosenProjectId);
      setStates(stream?.states ?? []);
      setSubtypes(stream?.subtypes ?? DEFAULT_WORK_ITEM_SUBTYPES);
      setSubtype('');
      const def = stream?.states.find((s) => s.is_default);
      if (!createWorkItem.stateId) setStateId(def?.id ?? '');
      return;
    }
    let cancelled = false;
    void Promise.all([
      apiFetch<{ items: StateDto[] }>(v1Path(`/projects/${chosenProjectId}/states`)),
      apiFetch<ProjectDto>(v1Path(`/projects/${chosenProjectId}`)),
      apiFetch<{ items?: MemberDto[] }>(v1Path(`/projects/${chosenProjectId}/members`)).catch(() => ({ items: [] })),
    ])
      .then(([res, project, members]) => {
        if (cancelled) return;
        const people = members.items ?? [];
        setTeamMembers(people);
        setOwnerIds((current) =>
          current.length === 0 && userId && people.some((member) => member.user_id === userId) ? [userId] : current,
        );
        setStates(res.items);
        setSubtypes(project.subtypes ?? DEFAULT_WORK_ITEM_SUBTYPES);
        setSubtype('');
        if (!createWorkItem.stateId) {
          const def = res.items.find((s) => s.is_default);
          if (def) setStateId(def.id);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStates([]);
          setSubtypes(DEFAULT_WORK_ITEM_SUBTYPES);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [createWorkItem.open, createWorkItem.stateId, chosenProjectId, launchStep, streams, userId]);

  async function closeRequested() {
    if (dirty) {
      const ok = await confirm({
        title: 'Discard draft?',
        description: 'You have unsaved fields. Close without creating?',
        confirmLabel: 'Discard',
        destructive: true,
      });
      if (!ok) return;
    }
    setCreateWorkItemOpen(false);
  }

  async function submit() {
    const trimmed = title.trim();
    if (!trimmed) return;
    if (!chosenProjectId) {
      toast('Choose a stream first.');
      return;
    }
    setBusy(true);
    try {
      if (createWorkItem.capture) {
        await createWorkItem.capture({
          title: trimmed,
          projectId: chosenProjectId,
          ownerIds,
          dueDate: goLiveDate,
          description,
        });
        toast(`Added “${trimmed}”`);
        if (createMore) {
          setTitle('');
          setDescription('');
          setGoLiveDate('');
        } else {
          setCreateWorkItemOpen(false);
        }
        return;
      }
      if (launchStep && initiativeId) {
        await createLaunchStep(initiativeId, {
          project_id: chosenProjectId,
          title: trimmed,
          description,
          ...(stateId ? { state_id: stateId } : {}),
          ...(subtype ? { subtype } : {}),
          ...(goLiveDate ? { due_date: goLiveDate } : {}),
          ...(ownerIds.length ? { assignee_ids: ownerIds } : {}),
        });
      } else {
        await apiFetch<WorkItemFull>(v1Path(`/projects/${chosenProjectId}/work-items`), {
          method: 'POST',
          body: JSON.stringify({
            title: trimmed,
            description,
            ...(stateId ? { state_id: stateId } : {}),
            ...(createWorkItem.parentId ? { parent_id: createWorkItem.parentId } : {}),
            type: itemType,
            ...(itemType === 'task' && subtype ? { subtype } : {}),
            ...(canLinkInitiative && initiativeId ? { initiative_id: initiativeId } : {}),
            ...(goLiveDate ? { due_date: goLiveDate } : {}),
            ...(ownerIds.length ? { assignee_ids: ownerIds } : {}),
          }),
        });
      }
      toast(`Created “${trimmed}”`);
      window.dispatchEvent(new CustomEvent('bb:work-items-changed'));
      if (createWorkItem.parentId) window.dispatchEvent(new CustomEvent('bb:subtask-created'));
      if (createMore) {
        setTitle('');
        setDescription('');
        setGoLiveDate('');
      } else {
        setCreateWorkItemOpen(false);
      }
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to create');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={createWorkItem.open}
      onOpenChange={(open) => {
        if (!open) void closeRequested();
      }}
    >
      <DialogContent
        className="max-h-[90dvh] overflow-y-auto"
        onPointerDownOutside={(e) => {
          if (dirty) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>{`Create ${workItemTypeLabel(itemType).toLowerCase()}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="wi-title">Title</Label>
            <Input
              id="wi-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !busy && title.trim() && chosenProjectId) void submit();
              }}
              autoFocus
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="wi-project">Stream</Label>
            <Select
              value={chosenProjectId}
              onValueChange={(value) => {
                setChosenProjectId(value);
                setOwnerIds([]);
              }}
            >
              <SelectTrigger id="wi-project">
                <SelectValue placeholder="Choose a stream" />
              </SelectTrigger>
              <SelectContent>
                {projects.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label>Owner</Label>
              {chosenProjectId ? (
                <div>
                  <OwnerPicker value={ownerIds} members={ownerOptions} onChange={setOwnerIds} />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Choose a stream first.</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="wi-go-live">Due date</Label>
              <Input
                id="wi-go-live"
                type="date"
                value={goLiveDate}
                onChange={(e) => setGoLiveDate(e.target.value)}
              />
            </div>
          </div>
          <details className="group rounded-lg border border-border/80 px-3 py-2">
            <summary className="cursor-pointer select-none text-sm font-medium text-muted-foreground hover:text-foreground">
              More details
            </summary>
            <div className="mt-3 space-y-3">
              <div className="space-y-1">
                <Label htmlFor="wi-desc">Description</Label>
                <RichTextField
                  key={itemType}
                  variant="compact"
                  value={description}
                  upload={(file) => uploadPendingAttachment(file).then((row) => row.download_url)}
                  onUploadError={toast}
                  onChange={setDescription}
                />
              </div>
              {lockedType ? null : (
                <div className="space-y-1">
                  <Label htmlFor="wi-type">Type</Label>
                  <Select
                    value={itemType}
                    onValueChange={(value) => {
                      const next = value as (typeof WORK_ITEM_TYPES)[number];
                      if (next === 'incident' || next === 'problem' || next === 'service_request') {
                        setCreateWorkItemOpen(false);
                        openCreateWorkItem({
                          type: next,
                          projectId: chosenProjectId || undefined,
                          stateId: stateId || undefined,
                        });
                        return;
                      }
                      setItemType(next);
                    }}
                  >
                    <SelectTrigger id="wi-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {WORK_ITEM_TYPES.filter((type) => type !== 'initiative').map((type) => (
                        <SelectItem key={type} value={type}>
                          {workItemTypeLabel(type)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {canLinkInitiative ? (
                <div className="space-y-1">
                  <Label>Initiative</Label>
                  <InitiativePicker value={initiativeId} onChange={setInitiativeId} className="w-full max-w-none" />
                </div>
              ) : null}
              {itemType === 'task' && subtypes.length > 0 ? (
                <div className="space-y-1">
                  <Label htmlFor="wi-subtype">{lockedType ? 'Type' : 'Task type'}</Label>
                  <Select
                    value={subtype || '__none__'}
                    onValueChange={(value) => setSubtype(value === '__none__' ? '' : (value as WorkItemSubtype))}
                  >
                    <SelectTrigger id="wi-subtype">
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {subtypes.map((option) => (
                        <SelectItem key={option.key} value={option.key}>
                          {option.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
              {itemType !== 'initiative' && states.length > 0 && stateId ? (
                <div className="space-y-1">
                  <Label htmlFor="wi-state">Status</Label>
                  <Select value={stateId} onValueChange={setStateId}>
                    <SelectTrigger id="wi-state">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {states.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
            </div>
          </details>
          <div className="flex items-center gap-2">
            <Checkbox
              id="wi-more"
              checked={createMore}
              onCheckedChange={(checked) => setCreateMore(checked === true)}
            />
            <Label htmlFor="wi-more">Add another after this one</Label>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => void closeRequested()}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={busy || !title.trim() || !chosenProjectId}
            onClick={() => void submit()}
          >
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
